import { jwtDecode } from "jwt-decode";
import { msalConfig, buildSsoSilentRequest } from "services/authProvider";
import Helper from "utils/helper";
import { getStoredMfaHomeAccountId, readMfaValidatedRaw } from "utils/mfaStorage";

export const MSAL_ID_TOKEN_KEY = "msal.idtoken";

/** Max wait for optional background `ssoSilent` — avoids long Edge iframe timeouts. */
const SSO_SILENT_BACKGROUND_MS = 4000;

/** @type {Promise<boolean> | null} */
let recoveryPromise = null;

/** Marker for accounts rebuilt from cached JWT when MSAL cache is empty. */
export const SYNTHETIC_ACCOUNT_MARKER = "__mxhhFromCache";

/**
 * @param {import("@azure/msal-browser").AccountInfo | null | undefined} account
 * @returns {boolean}
 */
export function isSyntheticMsalAccount(account) {
  return Boolean(account?.[SYNTHETIC_ACCOUNT_MARKER]);
}

/**
 * Decode login hint (email) from a cached B2C id token for `ssoSilent` recovery.
 * @param {string | null | undefined} idToken
 * @returns {string | undefined}
 */
export function getLoginHintFromIdToken(idToken) {
  if (!idToken || typeof idToken !== "string") {
    return undefined;
  }
  try {
    const payload = jwtDecode(idToken);
    const hint =
      payload.emails?.[0] ||
      payload.email ||
      payload.preferred_username ||
      payload.signInName ||
      payload.upn;
    return typeof hint === "string" && hint.length > 0 ? hint : undefined;
  } catch {
    return undefined;
  }
}

/**
 * @param {string | null | undefined} idToken
 * @returns {boolean}
 */
export function isCachedIdTokenExpired(idToken) {
  if (!idToken) {
    return true;
  }
  try {
    const payload = jwtDecode(idToken);
    return !payload.exp || payload.exp * 1000 <= Date.now();
  } catch {
    return true;
  }
}

/**
 * True when our persisted app session (cached id token + MFA remember-me gate) is
 * still within its custom policy lifecycle — independent of MSAL iframe renewal.
 *
 * @returns {boolean}
 */
export function isCustomAppSessionLifecycleValid() {
  const token = localStorage.getItem(MSAL_ID_TOKEN_KEY);
  if (!token || isCachedIdTokenExpired(token)) {
    return false;
  }
  return Helper.isLoggedIn() || readMfaValidatedRaw();
}

/**
 * Build a minimal MSAL {@link AccountInfo} from persisted app session keys so
 * the UI can render when Edge drops MSAL account cache but `msal.idtoken` +
 * `mfa-validate` remain.
 *
 * @returns {import("@azure/msal-browser").AccountInfo | null}
 */
export function buildAccountInfoFromCachedSession() {
  const idToken = localStorage.getItem(MSAL_ID_TOKEN_KEY);
  if (!idToken || isCachedIdTokenExpired(idToken)) {
    return null;
  }

  let claims;
  try {
    claims = jwtDecode(idToken);
  } catch {
    return null;
  }

  const localAccountId = claims.oid || claims.sub;
  const username = getLoginHintFromIdToken(idToken) || localAccountId;
  if (!localAccountId || !username) {
    return null;
  }

  const storedHomeId = getStoredMfaHomeAccountId();
  const policy = claims.acr || "b2c_1a_trustframeworkbase_susi";
  const tenantId = claims.tfid || claims.tid || "";
  const homeAccountId =
    storedHomeId ||
    (tenantId ? `${localAccountId}-${policy}.${tenantId}` : undefined);

  if (!homeAccountId) {
    return null;
  }

  let environment = "login.windows.net";
  try {
    if (claims.iss) {
      const host = new URL(claims.iss).hostname;
      environment = host.includes("b2clogin.com")
        ? host.split(".")[0]
        : host;
    }
  } catch {
    /* keep default */
  }

  return {
    homeAccountId,
    environment,
    tenantId,
    username,
    localAccountId,
    name: claims.name,
    idTokenClaims: claims,
    [SYNTHETIC_ACCOUNT_MARKER]: true,
  };
}

/**
 * Resolve the best available MSAL account: React hook → PCA cache → cached JWT.
 *
 * @param {import("@azure/msal-browser").IPublicClientApplication} instance
 * @param {import("@azure/msal-browser").AccountInfo | null | undefined} accountFromHook
 * @returns {import("@azure/msal-browser").AccountInfo | null}
 */
export function resolveMsalAccount(instance, accountFromHook) {
  if (accountFromHook) {
    return accountFromHook;
  }

  const active = instance.getActiveAccount();
  if (active) {
    return active;
  }

  const accounts = instance.getAllAccounts();
  if (accounts.length > 0) {
    return accounts[0];
  }

  return buildAccountInfoFromCachedSession();
}

/**
 * Synchronously attach a synthetic account when a valid cached id token exists.
 *
 * @param {import("@azure/msal-browser").IPublicClientApplication} instance
 * @returns {boolean}
 */
export function applySyntheticAccountFromCache(instance) {
  const synthetic = buildAccountInfoFromCachedSession();
  if (!synthetic) {
    return false;
  }
  try {
    instance.setActiveAccount(synthetic);
  } catch (e) {
    console.warn("MSAL recovery: setActiveAccount(synthetic) failed:", e);
    return false;
  }
  return true;
}

/**
 * Fast session recovery for bootstrap / first paint. Applies synthetic account
 * immediately when possible; optional `ssoSilent` runs in the background only.
 *
 * @param {import("@azure/msal-browser").IPublicClientApplication} instance
 * @returns {Promise<boolean>}
 */
export function recoverMsalSessionFromCache(instance) {
  if (!recoveryPromise) {
    recoveryPromise = recoverMsalSessionFromCacheInternal(instance);
  }
  return recoveryPromise;
}

/**
 * @param {import("@azure/msal-browser").IPublicClientApplication} instance
 * @returns {Promise<boolean>}
 */
async function recoverMsalSessionFromCacheInternal(instance) {
  const existing = resolveMsalAccount(instance, null);
  if (existing && !isSyntheticMsalAccount(existing)) {
    if (!instance.getActiveAccount()) {
      instance.setActiveAccount(existing);
    }
    return true;
  }

  const cachedToken = localStorage.getItem(MSAL_ID_TOKEN_KEY);
  if (!cachedToken || isCachedIdTokenExpired(cachedToken)) {
    return false;
  }

  if (applySyntheticAccountFromCache(instance)) {
    void attemptSsoSilentUpgrade(instance, cachedToken);
    return true;
  }

  return attemptSsoSilentUpgrade(instance, cachedToken);
}

/**
 * Try to replace synthetic account with a real MSAL cache entry (non-blocking).
 *
 * @param {import("@azure/msal-browser").IPublicClientApplication} instance
 * @param {string} cachedToken
 * @returns {Promise<boolean>}
 */
async function attemptSsoSilentUpgrade(instance, cachedToken) {
  let claims;
  try {
    claims = jwtDecode(cachedToken);
  } catch {
    return false;
  }

  const loginHint = getLoginHintFromIdToken(cachedToken);
  const sid = typeof claims.sid === "string" ? claims.sid : undefined;

  if (!sid && !loginHint) {
    return false;
  }

  const silentRequest = buildSsoSilentRequest({
    authority: msalConfig.auth.authority,
    ...(sid ? { sid } : { loginHint }),
  });

  try {
    const result = await promiseWithTimeout(
      instance.ssoSilent(silentRequest),
      SSO_SILENT_BACKGROUND_MS,
    );
    if (result?.account) {
      instance.setActiveAccount(result.account);
    }
    if (result?.idToken) {
      localStorage.setItem(MSAL_ID_TOKEN_KEY, result.idToken);
    }
    return instance.getAllAccounts().length > 0 || Boolean(result?.account);
  } catch (e) {
    console.warn("MSAL recovery: ssoSilent upgrade skipped or failed:", e);
    return applySyntheticAccountFromCache(instance);
  }
}

/**
 * @template T
 * @param {Promise<T>} promise
 * @param {number} ms
 * @returns {Promise<T>}
 */
function promiseWithTimeout(promise, ms) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`MSAL ssoSilent timed out after ${ms}ms`));
    }, ms);
    promise
      .then((value) => {
        clearTimeout(timer);
        resolve(value);
      })
      .catch((err) => {
        clearTimeout(timer);
        reject(err);
      });
  });
}

/**
 * Reset deduped recovery promise (for tests).
 */
export function resetMsalSessionRecoveryForTests() {
  recoveryPromise = null;
}
